import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  MoreHorizontal,
  Sparkles,
  Clock,
  Headphones,
  ArrowRight,
} from 'lucide-react';
import FontStyles from '../components/shared/FontStyles.jsx';
import FoxMark from '../components/shared/FoxMark.jsx';
import RootedWordmark from '../components/shared/RootedWordmark.jsx';
import NavMenu from '../components/shared/NavMenu.jsx';
import {
  PAPER,
  PAPER_2,
  INK,
  INK_MID,
  INK_SOFT,
  LINE,
  ACCENT,
  GOLD_DEEP,
} from '../components/shared/theme.js';
import { useApp } from '../context/AppContext.jsx';
import { useDayGeneration } from '../hooks/useDayGeneration.js';
import { clearAllData } from '../services/storage.js';

const DAY_COLORS = ['#5B2A2E', '#3A4A52', '#3C3842', '#32436B', '#7A5820'];
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

function PassageCard({ dayNumber, scriptureRef, color }) {
  return (
    <div
      className="relative rounded-[3px] overflow-hidden flex flex-col justify-between flex-shrink-0 grain"
      style={{
        backgroundColor: color || '#3A4A52',
        width: 84,
        height: 126,
        padding: 10,
        boxShadow:
          '0 6px 20px -8px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(255,255,255,0.04)',
      }}
    >
      <div
        className="text-white/55 ff-mono"
        style={{ fontSize: 8, letterSpacing: '0.15em' }}
      >
        DAY {dayNumber} · {DOW[dayNumber - 1] || ''}
      </div>

      <div style={{ marginTop: 7 }}>
        <div
          className="text-white ff-display leading-[0.95] font-medium"
          style={{ fontSize: 17, wordBreak: 'break-word' }}
        >
          {scriptureRef || 'Day ' + dayNumber}
        </div>
      </div>

      <span
        className="absolute left-0 right-0 bg-white/10"
        style={{ height: 1, top: 22 }}
      />
      <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-white/10" />
    </div>
  );
}

function MissingStudy({ onRestart }) {
  return (
    <div
      className="min-h-screen w-full ff-ui grain flex flex-col items-center justify-center px-9 text-center"
      style={{ background: PAPER, color: INK, position: 'relative' }}
    >
      <FontStyles />
      <FoxMark size={72} variant="gold" />
      <span className="block hairline-gold h-px w-32 mt-6" />
      <h2
        className="ff-display mt-6"
        style={{ fontSize: 26, fontWeight: 400, color: INK, lineHeight: 1.1 }}
      >
        Your study isn't ready yet.
      </h2>
      <p
        className="ff-display italic mt-4 max-w-[300px] leading-[1.5]"
        style={{ color: INK_MID, fontSize: 15 }}
      >
        Looks like we lost the thread. Let's start the conversation again — it
        only takes a few minutes.
      </p>
      <button
        onClick={onRestart}
        className="mt-8 py-3 px-8 rounded-full ff-ui font-medium text-[14px] flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
        style={{ background: INK, color: PAPER }}
      >
        Restart with my guide
        <ArrowRight size={16} strokeWidth={1.75} />
      </button>
    </div>
  );
}

export default function StudyPlan() {
  const navigate = useNavigate();
  const { weekNumber: weekParam } = useParams();
  const {
    study,
    weeks,
    getWeek,
    getCurrentWeekNumber,
    userProfile,
    guide,
    isDayComplete,
    completedDays,
  } = useApp();
  const { generateDay, isGenerating } = useDayGeneration();

  const currentWeekNumber = weekParam
    ? parseInt(weekParam, 10)
    : getCurrentWeekNumber();
  const weekData =
    getWeek(currentWeekNumber) ||
    (currentWeekNumber === 1 ? study : null);

  // After a day is completed in the current week, generate the next one
  // in the background. No-op until at least one day of this week is done.
  useEffect(() => {
    if (!weekData) return;
    const have = (weekData.days || []).map(
      (d) => d.dayNumber || d.day_number
    );
    const completedThisWeek = (completedDays || [])
      .filter((d) => (d.weekNumber || 1) === currentWeekNumber)
      .map((d) => d.dayNumber);
    if (completedThisWeek.length === 0) return;
    const lastCompleted = Math.max(...completedThisWeek);
    const nextNeeded = lastCompleted + 1;
    if (nextNeeded <= 5 && !have.includes(nextNeeded)) {
      console.log(
        '[StudyPlan] Triggering generation of Week',
        currentWeekNumber,
        'Day',
        nextNeeded
      );
      generateDay(nextNeeded, currentWeekNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedDays, weekData?.days?.length, currentWeekNumber]);

  if (!weekData || !Array.isArray(weekData.days) || weekData.days.length === 0) {
    return <MissingStudy onRestart={() => navigate('/intake')} />;
  }

  const enneaLabel = userProfile?.enneagram_type
    ? `Type ${userProfile.enneagram_type}${
        userProfile.enneagram_wing ? `w${userProfile.enneagram_wing}` : ''
      }`
    : 'Your Path';

  return (
    <div
      className="min-h-screen w-full ff-ui flex flex-col"
      style={{ background: PAPER, color: INK, position: 'relative' }}
    >
      <FontStyles />

      {/* Header */}
      <div
        className="flex items-center justify-between px-6 pt-5 pb-1 w-full max-w-[460px] mx-auto"
        style={{ flexShrink: 0 }}
      >
        <button
          onClick={() => navigate('/')}
          className="p-2 -ml-2 rounded-full transition active:scale-95"
        >
          <ChevronLeft size={20} strokeWidth={1.5} style={{ color: INK }} />
        </button>
        <div className="flex items-center gap-2">
          <FoxMark size={14} />
          <RootedWordmark size="sm" />
        </div>
        <NavMenu />
      </div>

      <div
        className="flex-1 overflow-y-auto scrollbar-hide w-full max-w-[460px] mx-auto"
        style={{ paddingBottom: 32 }}
      >
        {/* Nameplate */}
        <div className="px-7 pt-6 pb-5">
          <div className="flex items-center justify-between">
            <div
              className="ff-mono"
              style={{ color: ACCENT, fontSize: 10, letterSpacing: '0.18em' }}
            >
              WEEK 1 · YOUR FIRST FIVE
            </div>
            <div
              className="ff-mono"
              style={{
                color: INK_SOFT,
                fontSize: 10,
                letterSpacing: '0.12em',
              }}
            >
              5 DAYS
            </div>
          </div>

          <div className="mt-6 relative">
            <span className="block hairline-gold h-px w-full" />
            <div className="py-5 text-center">
              <div
                className="widecaps ff-ui"
                style={{ color: INK_MID, fontSize: 10 }}
              >
                Prepared for
              </div>
              <div
                className="ff-display mt-2 leading-none"
                style={{
                  color: INK,
                  fontSize: 44,
                  fontWeight: 300,
                  letterSpacing: '0.04em',
                }}
              >
                {userProfile?.name || 'You'}
              </div>
              <div className="flex items-center justify-center gap-2 mt-3">
                <span
                  className="w-1 h-1 rounded-full"
                  style={{ background: ACCENT }}
                />
                <span
                  className="ff-mono"
                  style={{
                    color: ACCENT,
                    fontSize: 10,
                    letterSpacing: '0.22em',
                  }}
                >
                  {enneaLabel.toUpperCase()}
                </span>
                <span
                  className="w-1 h-1 rounded-full"
                  style={{ background: ACCENT }}
                />
              </div>
            </div>
            <span className="block hairline-gold h-px w-full" />
          </div>
        </div>

        {/* Week title + theme */}
        <div className="px-7 pt-2 pb-6">
          <h1
            className="ff-display leading-[1.0]"
            style={{ color: INK, fontSize: 36, fontWeight: 300 }}
          >
            {study.week_title || 'Your First Week.'}
          </h1>
          {study.week_theme && (
            <div
              className="ff-display italic mt-3"
              style={{ color: INK_MID, fontSize: 15 }}
            >
              {study.week_theme}
            </div>
          )}

          {guide?.name && (
            <p
              className="ff-display italic mt-5 leading-[1.45]"
              style={{ color: INK_MID, fontSize: 13.5 }}
            >
              Built by {guide.name}, drawn from everything you shared.
            </p>
          )}

          <div
            className="mt-5 p-4 rounded-lg"
            style={{
              background: 'rgba(166,124,58,0.08)',
              border: `1px solid rgba(166,124,58,0.22)`,
            }}
          >
            <p
              className="ff-display italic text-center leading-[1.45]"
              style={{ color: GOLD_DEEP, fontSize: 13 }}
            >
              "Each day is designed for 15–20 minutes.
              <br />
              Bring your honest self."
            </p>
          </div>
        </div>

        <div className="hairline h-px mx-7" />

        <div className="px-7 pt-5 pb-3 flex items-center justify-between">
          <div className="smallcaps ff-ui" style={{ color: INK_SOFT }}>
            The five days
          </div>
          <div className="ff-mono text-[10px]" style={{ color: INK_SOFT }}>
            ~ 90 MIN · ALL UNLOCKED
          </div>
        </div>

        <div className="px-7 space-y-3 pb-6">
          {[1, 2, 3, 4, 5].map((num) => {
            const idx = num - 1;
            const day = weekData.days.find(
              (d) => (d.dayNumber || d.day_number) === num
            );
            const isComplete = isDayComplete(num, currentWeekNumber);
            const isPreparing = !day && isGenerating;
            const isLocked = !day && !isPreparing;
            const color = DAY_COLORS[idx] || DAY_COLORS[idx % 5];

            const cardOpacity = isComplete
              ? 0.85
              : isLocked
                ? 0.5
                : isPreparing
                  ? 0.7
                  : 1;

            const dayName =
              (day?.day_name || DOW[idx] || `Day ${num}`).toUpperCase();

            const innerContent = (
              <>
                <PassageCard
                  dayNumber={num}
                  scriptureRef={day?.scripture_reference || ''}
                  color={color}
                />
                <div className="flex-1 flex flex-col py-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="ff-mono"
                      style={{
                        color: INK_SOFT,
                        fontSize: 10,
                        letterSpacing: '0.1em',
                      }}
                    >
                      {dayName}
                    </span>
                    {isComplete ? (
                      <span
                        className="ff-ui px-2 py-0.5 rounded-full font-medium"
                        style={{
                          fontSize: 9,
                          background: 'rgba(166,124,58,0.18)',
                          color: GOLD_DEEP,
                          letterSpacing: '0.05em',
                          border: '1px solid rgba(166,124,58,0.3)',
                        }}
                      >
                        ✓ COMPLETE
                      </span>
                    ) : isPreparing ? (
                      <span
                        className="ff-ui px-2 py-0.5 rounded-full font-medium"
                        style={{
                          fontSize: 9,
                          background: 'rgba(166,124,58,0.12)',
                          color: GOLD_DEEP,
                          letterSpacing: '0.05em',
                          fontStyle: 'italic',
                        }}
                      >
                        PREPARING…
                      </span>
                    ) : isLocked ? (
                      <span
                        className="ff-ui px-2 py-0.5 rounded-full font-medium"
                        style={{
                          fontSize: 9,
                          background: 'rgba(27,24,20,0.06)',
                          color: INK_SOFT,
                          letterSpacing: '0.05em',
                        }}
                      >
                        COMING SOON
                      </span>
                    ) : (
                      <span
                        className="ff-ui px-2 py-0.5 rounded-full font-medium"
                        style={{
                          fontSize: 9,
                          background: 'rgba(166,124,58,0.12)',
                          color: GOLD_DEEP,
                          letterSpacing: '0.05em',
                        }}
                      >
                        UNLOCKED
                      </span>
                    )}
                  </div>
                  <div
                    className="ff-display mt-1 leading-[1.1]"
                    style={{ color: INK, fontSize: 16, fontWeight: 500 }}
                  >
                    {day?.title || `Day ${num}`}
                  </div>
                  {day?.subtitle ? (
                    <div
                      className="ff-display italic mt-0.5 leading-snug"
                      style={{ color: INK_MID, fontSize: 12 }}
                    >
                      {day.subtitle}
                    </div>
                  ) : isLocked ? (
                    <div
                      className="ff-display italic mt-0.5 leading-snug"
                      style={{ color: INK_SOFT, fontSize: 12 }}
                    >
                      Available after you complete Day {num - 1}
                    </div>
                  ) : isPreparing ? (
                    <div
                      className="ff-display italic mt-0.5 leading-snug"
                      style={{ color: INK_SOFT, fontSize: 12 }}
                    >
                      Drawing from what you shared in Day {num - 1}…
                    </div>
                  ) : null}
                  <div className="mt-auto flex items-center gap-3 pt-2">
                    <div
                      className="ff-ui flex items-center gap-3"
                      style={{ color: INK_SOFT, fontSize: 10 }}
                    >
                      <span className="flex items-center gap-1">
                        <Clock size={10} strokeWidth={1.5} />
                        15–20 min
                      </span>
                      <span>·</span>
                      <span className="flex items-center gap-1">
                        <Headphones size={10} strokeWidth={1.5} />
                        Audio
                      </span>
                    </div>
                    {day && (
                      <span
                        className="ff-ui ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full"
                        style={{
                          background: isComplete ? 'transparent' : INK,
                          color: isComplete ? GOLD_DEEP : PAPER,
                          border: isComplete
                            ? '1px solid rgba(166,124,58,0.4)'
                            : 'none',
                          fontSize: 10,
                          fontWeight: 500,
                        }}
                      >
                        {isComplete ? 'Review' : 'Begin'}
                        <ArrowRight size={11} strokeWidth={1.75} />
                      </span>
                    )}
                  </div>
                </div>
              </>
            );

            if (day) {
              return (
                <button
                  key={num}
                  type="button"
                  onClick={() =>
                    navigate(
                      currentWeekNumber === 1
                        ? `/study/day/${num}`
                        : `/study/week/${currentWeekNumber}/day/${num}`
                    )
                  }
                  className="w-full text-left flex items-stretch gap-4 group transition-all active:scale-[0.99]"
                  style={{ opacity: cardOpacity }}
                >
                  {innerContent}
                </button>
              );
            }
            return (
              <div
                key={num}
                className="w-full text-left flex items-stretch gap-4"
                style={{ opacity: cardOpacity, cursor: 'default' }}
              >
                {innerContent}
              </div>
            );
          })}
        </div>

        <div className="hairline h-px mx-7" />

        <div className="px-7 py-6">
          <div
            className="rounded-2xl p-5 grain relative overflow-hidden"
            style={{
              background: 'rgba(166,124,58,0.08)',
              border: `1px solid rgba(166,124,58,0.25)`,
            }}
          >
            <div
              className="flex items-center gap-2 smallcaps ff-ui"
              style={{ color: GOLD_DEEP }}
            >
              <Sparkles size={11} strokeWidth={1.75} />
              The through-line
            </div>
            <p
              className="ff-display italic mt-3 leading-[1.35]"
              style={{ color: INK, fontSize: 15, fontWeight: 400 }}
            >
              Each day adapts to the one before. Your check-ins shape what
              comes next.
            </p>
          </div>
        </div>

        {/* Brand footer */}
        <div className="px-7 pb-12 flex flex-col items-center gap-2">
          <FoxMark size={24} variant="gold-deep" />
          <div
            className="ff-display italic text-center"
            style={{ color: INK_SOFT, fontSize: 11 }}
          >
            It's not about who you are.
            <br />
            <span style={{ fontSize: 10 }}>It's who you are becoming.</span>
          </div>

          <button
            type="button"
            onClick={() => {
              if (
                window.confirm(
                  'Start a new study? This will clear your current one.'
                )
              ) {
                clearAllData();
                navigate('/');
                // Force a reload so AppContext re-hydrates from empty storage
                if (typeof window !== 'undefined') {
                  setTimeout(() => window.location.reload(), 50);
                }
              }
            }}
            style={{
              background: 'none',
              border: 'none',
              color: INK,
              opacity: 0.4,
              fontSize: 13,
              cursor: 'pointer',
              marginTop: 24,
            }}
          >
            Start a new study
          </button>
        </div>
      </div>
    </div>
  );
}
